/** SEO content rendering components — Card-based deep-dive sections and FAQ accordion. */

import { useEffect, useRef, useState } from "react";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn, formatSentenceCaseTitle } from "@/lib/utils";

export interface SeoRenderableSubsection {
	id: string;
	title: string;
	paragraphs: string[];
	orderedList?: string[];
	unorderedList?: string[];
}

export interface SeoRenderableSection {
	id: string;
	title: string;
	subsections: SeoRenderableSubsection[];
}

export interface SeoRenderableFaqItem {
	id: string;
	question: string;
	answer: string;
}

export interface SeoOutlineItem {
	label: string;
	targetId: string;
}

/**
 * Renders deep-dive sections as individual Cards.
 * Sections beyond `collapsedAfter` show title + excerpt with a "See more" toggle.
 * Collapsed content uses `forceMount` so Googlebot can still crawl it.
 */
export function SeoLongFormContent({
	sections,
	onSectionVisible,
	collapsedAfter,
}: {
	sections: SeoRenderableSection[];
	onSectionVisible?: (sectionId: string) => void;
	collapsedAfter?: number;
}) {
	const seenRef = useRef(new Set<string>());
	const callbackRef = useRef(onSectionVisible);
	callbackRef.current = onSectionVisible;
	const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

	const toggleSection = (id: string) => {
		setExpandedSections((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	// Fire visibility for the first section on mount.
	useEffect(() => {
		const firstId = sections[0]?.id;
		if (firstId && callbackRef.current && !seenRef.current.has(firstId)) {
			seenRef.current.add(firstId);
			callbackRef.current(firstId);
		}
	}, [sections]);

	// Track remaining sections via IntersectionObserver.
	useEffect(() => {
		if (!callbackRef.current || typeof IntersectionObserver === "undefined") return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					const id = (entry.target as HTMLElement).dataset.sectionId;
					if (!id || seenRef.current.has(id)) continue;
					seenRef.current.add(id);
					callbackRef.current?.(id);
				}
			},
			{ threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
		);

		const nodes = document.querySelectorAll("[data-section-id]");
		for (const node of nodes) observer.observe(node);

		return () => observer.disconnect();
	}, []);

	return (
		<div className="space-y-6">
			{sections.map((section, sectionIndex) => {
				const isCollapsible = collapsedAfter !== undefined && sectionIndex >= collapsedAfter;
				const isExpanded = expandedSections.has(section.id);
				const excerpt = section.subsections[0]?.paragraphs[0];

				return (
					<Card key={section.id} data-section-id={section.id}>
						<CardHeader>
							<CardTitle className="text-2xl tracking-tight sm:text-3xl">
								{formatSentenceCaseTitle(section.title)}
							</CardTitle>
							{isCollapsible && !isExpanded && excerpt && (
								<p className="mt-2 text-base leading-relaxed text-muted-foreground">{excerpt}</p>
							)}
						</CardHeader>

						{isCollapsible ? (
							<Collapsible open={isExpanded} onOpenChange={() => toggleSection(section.id)}>
								<CollapsibleContent forceMount className="data-[state=closed]:hidden">
									<CardContent>
										<SubsectionContent subsections={section.subsections} />
									</CardContent>
								</CollapsibleContent>
								<div className="px-6 pb-4">
									<CollapsibleTrigger asChild>
										<button
											type="button"
											data-testid={`seo-read-more-${section.id}`}
											className="text-sm font-medium text-primary hover:underline"
										>
											{isExpanded ? "See less" : "See more"}
										</button>
									</CollapsibleTrigger>
								</div>
							</Collapsible>
						) : (
							<CardContent>
								<SubsectionContent subsections={section.subsections} />
							</CardContent>
						)}
					</Card>
				);
			})}
		</div>
	);
}

/** Renders the subsections within a single Card. */
function SubsectionContent({ subsections }: { subsections: SeoRenderableSubsection[] }) {
	return (
		<div className="space-y-8">
			{subsections.map((sub) => (
				<div key={sub.id} className="space-y-3">
					<h3 className="text-xl font-semibold text-foreground">
						{formatSentenceCaseTitle(sub.title)}
					</h3>
					{sub.paragraphs.map((p, i) => (
						<p key={`${sub.id}-p-${i}`} className="text-base leading-relaxed text-muted-foreground">
							{p}
						</p>
					))}
					{Boolean(sub.orderedList?.length) && (
						<ol className="list-decimal space-y-2 pl-5 text-base leading-relaxed text-muted-foreground">
							{sub.orderedList?.map((item, i) => (
								<li key={`${sub.id}-ol-${i}`}>{item}</li>
							))}
						</ol>
					)}
					{Boolean(sub.unorderedList?.length) && (
						<ul className="list-disc space-y-2 pl-5 text-base leading-relaxed text-muted-foreground">
							{sub.unorderedList?.map((item, i) => (
								<li key={`${sub.id}-ul-${i}`}>{item}</li>
							))}
						</ul>
					)}
				</div>
			))}
		</div>
	);
}

export function SeoFaqSection({
	items,
	className,
	title = "FAQ",
	description = "Answers to implementation and rollout questions teams ask most often.",
}: {
	items: SeoRenderableFaqItem[];
	className?: string;
	title?: string;
	description?: string;
}) {
	return (
		<section id="faq" data-testid="seo-faq-section" className={cn("space-y-6", className)}>
			<div className="space-y-3 text-center">
				<h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
				<p className="mx-auto max-w-[60ch] text-lg text-muted-foreground">{description}</p>
			</div>
			<Accordion type="single" collapsible className="w-full">
				{items.map((item) => (
					<AccordionItem key={item.id} value={item.id}>
						<AccordionTrigger className="py-5 text-left text-base font-semibold hover:no-underline">
							{item.question}
						</AccordionTrigger>
						<AccordionContent className="pb-5 text-base leading-relaxed text-muted-foreground">
							<p>{item.answer}</p>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
		</section>
	);
}
